import React, { useState } from 'react';
import GlassCard from '../charts/GlassCard';
import { BarChart } from '../charts/AnalyticsCharts';

export const ENROLLMENT_PERIODS = [
  { key: 'today', label: 'Today', courses: 'todayCourses' },
  { key: 'weekly', label: 'This Week', courses: 'weeklyCourses' },
  { key: 'monthly', label: 'This Month', courses: 'monthlyCourses' },
];
export const ENROLLMENT_CATEGORIES = [
  { key: 'college_free', label: 'Skill Share Free Enrollment', color: '#15803d' },
  { key: 'course', label: 'Course', color: '#2563eb' },
];

export function enrollmentSummaryText(categories = []) {
  return ['Enrollments', ...ENROLLMENT_PERIODS.flatMap(period => [period.label,
    ...ENROLLMENT_CATEGORIES.map(category => {
      const item = categories.find(row => row.category === category.key);
      const courses = item?.[period.courses]?.map(course => `${course.count} - ${course.courseName}`).join(', ');
      return `${category.label}: ${item?.[period.key] ?? '-'}\n${courses || (item?.[period.key] === 0 ? 'No enrollments' : 'Unavailable')}`;
    }), '',
  ])].join('\n');
}

export default function EnrollmentSummaryCard({ categories = [] }) {
  const [copyStatus, setCopyStatus] = useState('');
  const [expandedPeriods, setExpandedPeriods] = useState({ weekly: false, monthly: false });
  const complete = ENROLLMENT_CATEGORIES.every(category => {
    const item = categories.find(row => row.category === category.key);
    return ENROLLMENT_PERIODS.every(period => Number.isFinite(item?.[period.key]) && Array.isArray(item?.[period.courses]));
  });
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(enrollmentSummaryText(categories));
      setCopyStatus('Copied');
    } catch (_) { setCopyStatus('Copy failed. Please allow clipboard access.'); }
  };
  return <GlassCard title="Enrollments" subtitle="Free and course enrollments" icon="bi-person-plus-fill" accent="#22c55e"
    extra={<button type="button" className="btn btn-sm btn-outline-secondary" disabled={!complete} onClick={copy} title="Copy enrollment summary">
      <i className={`bi ${copyStatus === 'Copied' ? 'bi-check2' : 'bi-copy'} me-1`} />{copyStatus === 'Copied' ? 'Copied' : 'Copy'}
    </button>}>
    {copyStatus && <div className="small text-muted mb-2" role="status">{copyStatus}</div>}
    {ENROLLMENT_PERIODS.map(period => <section className="border-bottom pb-3 mb-3" key={period.key} aria-label={period.label}>
      <div className="d-flex align-items-center justify-content-between gap-2 mb-2">
        <div className="fw-semibold small text-uppercase">{period.label}</div>
        {period.key !== 'today' && <button type="button" className="btn btn-sm btn-outline-secondary py-1"
          aria-expanded={expandedPeriods[period.key]}
          aria-label={`${expandedPeriods[period.key] ? 'Hide' : 'Show'} ${period.label.toLowerCase()} course breakdown`}
          onClick={() => setExpandedPeriods(current => ({ ...current, [period.key]: !current[period.key] }))}>
          {expandedPeriods[period.key] ? 'Hide' : 'Show'}<i className={`bi ${expandedPeriods[period.key] ? 'bi-chevron-up' : 'bi-chevron-down'} ms-1`} />
        </button>}
      </div>
      <div className="row g-2">
        {ENROLLMENT_CATEGORIES.map((category, index) => {
          const item = categories.find(row => row.category === category.key);
          const courses = item?.[period.courses]?.map(course => `${course.count} - ${course.courseName}`).join(', ');
          return <div className={`col-6 ${index ? 'border-start' : ''}`} style={{ minWidth: 0, overflowWrap: 'anywhere' }} key={category.key}>
            <button type="button" className="btn btn-link text-decoration-none text-start p-0 fw-semibold"
              style={{ color: category.color, whiteSpace: 'normal' }}
              onClick={() => window.location.assign(`/enrollments?category=${category.key}`)}>{category.label}</button>
            <div className="fs-4 fw-bold">{item?.[period.key] ?? '-'}</div>
            <div className="small text-muted" hidden={period.key !== 'today' && !expandedPeriods[period.key]}>
              {courses || (item?.[period.key] === 0 ? 'No enrollments' : 'Breakdown unavailable')}
            </div>
          </div>;
        })}
      </div>
    </section>)}
    <div className="fw-semibold mb-2">Enrollment Comparison</div>
    {complete ? <BarChart height={220} labels={ENROLLMENT_PERIODS.map(period => period.label)}
      datasets={ENROLLMENT_CATEGORIES.map(category => ({ label: category.label, color: category.color,
        data: ENROLLMENT_PERIODS.map(period => categories.find(row => row.category === category.key)[period.key]),
      }))} /> : <div className="small text-muted">Enrollment comparison is unavailable until all periods are loaded.</div>}
  </GlassCard>;
}
